import { getSellerSubmissionPublicFieldLabels } from "../../../utils/sellerIntake/sellerIntakeDefaults";
import { SellerFieldError, sellerFieldDescribedBy, sellerFieldInvalid, sellerIntakeFieldFocusHandler } from "./sellerIntakeFormFieldUtils";
import { useSellerIntakeCopy } from "../../../intl/productFormCopy.js";

const labels = getSellerSubmissionPublicFieldLabels();

function RequiredMark() {
  return (
    <span className="sell-car-form__required" aria-hidden="true">
      *
    </span>
  );
}

/**
 * @param {{
 *   input: import('../../../utils/sellerIntake/sellerIntakeTypes').SellerSubmissionInput,
 *   onChange: (patch: Partial<import('../../../utils/sellerIntake/sellerIntakeTypes').SellerSubmissionInput>) => void,
 *   errors?: Record<string, string|null|undefined>,
 *   disabled?: boolean,
 * }} props
 */
export default function SellerContactFields({ input, onChange, errors = {}, disabled = false }) {
  const copy = useSellerIntakeCopy();
  return (
    <fieldset className="sell-car-form__section-card" disabled={disabled}>
      <legend className="sell-car-form__section-title">{copy.contactSectionTitle}</legend>

      <div className="sell-car-form__grid">
        <div className="sell-car-form__field sell-car-form__field--full">
          <label htmlFor="seller-intake-seller-type">
            {labels.sellerType} <RequiredMark />
          </label>
          <select
            id="seller-intake-seller-type"
            name="sellerType"
            value={input.sellerType}
            aria-invalid={sellerFieldInvalid("sellerType", errors)}
            aria-describedby={sellerFieldDescribedBy("sellerType", errors)}
            onChange={(event) => onChange({ sellerType: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          >
            {copy.sellerTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <SellerFieldError fieldKey="sellerType" message={errors.sellerType} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-seller-name">
            {labels.sellerName} <RequiredMark />
          </label>
          <input
            id="seller-intake-seller-name"
            name="sellerName"
            type="text"
            autoComplete="name"
            enterKeyHint="next"
            value={input.sellerName}
            aria-invalid={sellerFieldInvalid("sellerName", errors)}
            aria-describedby={sellerFieldDescribedBy("sellerName", errors)}
            onChange={(event) => onChange({ sellerName: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="sellerName" message={errors.sellerName} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-seller-phone">
            {labels.sellerPhone} <RequiredMark />
          </label>
          <input
            id="seller-intake-seller-phone"
            name="sellerPhone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            enterKeyHint="next"
            value={input.sellerPhone}
            aria-invalid={sellerFieldInvalid("sellerPhone", errors)}
            aria-describedby={sellerFieldDescribedBy("sellerPhone", errors)}
            onChange={(event) => onChange({ sellerPhone: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="sellerPhone" message={errors.sellerPhone} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-seller-email">{labels.sellerEmail}</label>
          <input
            id="seller-intake-seller-email"
            name="sellerEmail"
            type="email"
            inputMode="email"
            autoComplete="email"
            enterKeyHint="next"
            value={input.sellerEmail}
            aria-invalid={sellerFieldInvalid("sellerEmail", errors)}
            aria-describedby={sellerFieldDescribedBy("sellerEmail", errors)}
            onChange={(event) => onChange({ sellerEmail: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="sellerEmail" message={errors.sellerEmail} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-contact-method">{labels.preferredContactMethod}</label>
          <select
            id="seller-intake-contact-method"
            name="preferredContactMethod"
            value={input.preferredContactMethod}
            aria-invalid={sellerFieldInvalid("preferredContactMethod", errors)}
            aria-describedby={sellerFieldDescribedBy("preferredContactMethod", errors)}
            onChange={(event) => onChange({ preferredContactMethod: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          >
            {copy.contactMethodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <SellerFieldError fieldKey="preferredContactMethod" message={errors.preferredContactMethod} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-city">
            {labels.city} <RequiredMark />
          </label>
          <input
            id="seller-intake-city"
            name="city"
            type="text"
            autoComplete="address-level2"
            enterKeyHint="next"
            value={input.city}
            aria-invalid={sellerFieldInvalid("city", errors)}
            aria-describedby={sellerFieldDescribedBy("city", errors)}
            onChange={(event) => onChange({ city: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="city" message={errors.city} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-department">{labels.department}</label>
          <input
            id="seller-intake-department"
            name="department"
            type="text"
            autoComplete="address-level1"
            enterKeyHint="next"
            value={input.department}
            aria-invalid={sellerFieldInvalid("department", errors)}
            aria-describedby={sellerFieldDescribedBy("department", errors)}
            onChange={(event) => onChange({ department: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="department" message={errors.department} />
        </div>
      </div>
    </fieldset>
  );
}
